'use client'

import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import {
  LuChevronLeft,
  LuChevronRight,
  LuChevronsLeft,
  LuChevronsRight,
} from 'react-icons/lu'

import { cn } from '@/core/utils/common'

import type { ReactNode } from 'react'
import type { Pagination as PaginationType } from '@/core/types/pagination'

type PaginationProps = {
  pagination: PaginationType
  pageKey?: string
  maxVisiblePages?: number
  className?: string
}

const PAGE_LINK_CLASS =
  'flex size-9 shrink-0 items-center justify-center rounded-field text-c13 transition-colors over4:size-10 over4:text-c14'

function PageLink({
  href,
  label,
  disabled,
  children,
  className,
}: {
  href: `?${string}`
  label: string
  disabled?: boolean
  children: ReactNode
  className?: string
}) {
  const styles = cn(
    PAGE_LINK_CLASS,
    'text-description',
    disabled
      ? 'cursor-not-allowed opacity-30'
      : 'cursor-pointer hover:bg-surface-strong hover:text-foreground focus:outline-none focus-visible:bg-surface-strong',
    className
  )

  if (disabled) {
    return (
      <span
        role="link"
        aria-label={label}
        aria-disabled="true"
        className={styles}
      >
        {children}
      </span>
    )
  }

  return (
    <Link href={href} scroll={false} aria-label={label} className={styles}>
      {children}
    </Link>
  )
}

export default function Pagination({
  pagination,
  pageKey = 'page',
  maxVisiblePages = 5,
  className,
}: PaginationProps) {
  const searchParams = useSearchParams()
  const { page_number, page_count } = pagination
  if (page_count <= 1) return null

  const currentPage = Math.min(page_count, Math.max(1, page_number))
  const visibleCount = Number.isFinite(maxVisiblePages)
    ? Math.max(1, Math.min(page_count, Math.floor(maxVisiblePages)))
    : 5
  const start = Math.max(
    1,
    Math.min(
      currentPage - Math.floor(visibleCount / 2),
      page_count - visibleCount + 1
    )
  )
  const pages = Array.from(
    { length: Math.min(visibleCount, page_count) },
    (_, index) => start + index
  )

  function pageHref(page: number): `?${string}` {
    const params = new URLSearchParams(searchParams.toString())
    params.set(pageKey, String(page))
    return `?${params.toString()}`
  }

  return (
    <nav
      aria-label="페이지 이동"
      className={cn(
        'flex min-w-0 items-center justify-center gap-1',
        className
      )}
    >
      <PageLink
        href={pageHref(1)}
        label="첫 페이지"
        disabled={currentPage === 1}
        className="hidden over8:flex"
      >
        <LuChevronsLeft aria-hidden="true" className="size-4" />
      </PageLink>
      <PageLink
        href={pageHref(currentPage - 1)}
        label="이전 페이지"
        disabled={currentPage === 1}
      >
        <LuChevronLeft aria-hidden="true" className="size-4" />
      </PageLink>

      <span
        className="px-3 text-c14 tabular-nums text-foreground over8:hidden"
        aria-label={`${page_count}페이지 중 ${currentPage}페이지`}
        aria-current="page"
      >
        {currentPage} / {page_count}
      </span>
      <div className="hidden min-w-0 items-center gap-1 over8:flex">
        {pages[0] > 1 && (
          <PageLink href={pageHref(1)} label="1페이지">
            1
          </PageLink>
        )}
        {pages[0] > 2 && (
          <span aria-hidden="true" className="px-1 text-description">
            …
          </span>
        )}
        {pages.map((page) =>
          page === currentPage ? (
            <span
              key={page}
              aria-current="page"
              aria-label={`${page}페이지`}
              className={cn(
                PAGE_LINK_CLASS,
                'cursor-default bg-point font-bold text-inverse'
              )}
            >
              {page}
            </span>
          ) : (
            <PageLink key={page} href={pageHref(page)} label={`${page}페이지`}>
              {page}
            </PageLink>
          )
        )}
        {pages[pages.length - 1] < page_count - 1 && (
          <span aria-hidden="true" className="px-1 text-description">
            …
          </span>
        )}
        {pages[pages.length - 1] < page_count && (
          <PageLink href={pageHref(page_count)} label={`${page_count}페이지`}>
            {page_count}
          </PageLink>
        )}
      </div>

      <PageLink
        href={pageHref(currentPage + 1)}
        label="다음 페이지"
        disabled={currentPage === page_count}
      >
        <LuChevronRight aria-hidden="true" className="size-4" />
      </PageLink>
      <PageLink
        href={pageHref(page_count)}
        label="마지막 페이지"
        disabled={currentPage === page_count}
        className="hidden over8:flex"
      >
        <LuChevronsRight aria-hidden="true" className="size-4" />
      </PageLink>
    </nav>
  )
}
